import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaHandoffHubV1Service, serviceName } from '../service';
import { GetHandoffSnapshotsByLocationIDIO, ServiceQueries } from '../types';
import { useHandoffHubV1Queries } from './use-handoff-hub-v1-queries';
import { UseHandoffHubV1QueryEndpointArgs, useHandoffHubV1Query } from './use-handoff-hub-v1-query';

/**
 * A hook that returns a query for the `GetHandoffSnapshotsByLocationID` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetHandoffSnapshotsByLocationIDQuery = <E = unknown, D = GetHandoffSnapshotsByLocationIDIO['output']>(
  args: UseHandoffHubV1QueryEndpointArgs<'GetHandoffSnapshotsByLocationID', E, D>
) =>
  useHandoffHubV1Query({
    endpointName: 'GetHandoffSnapshotsByLocationID',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetHandoffSnapshotsByLocationID` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetHandoffSnapshotsByLocationIDQueries = <E = unknown, D = GetHandoffSnapshotsByLocationIDIO['output']>(
  argsArr: UseHandoffHubV1QueryEndpointArgs<'GetHandoffSnapshotsByLocationID', E, D>[]
) =>
  useHandoffHubV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetHandoffSnapshotsByLocationID',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetHandoffSnapshotsByLocationID` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetHandoffSnapshotsByLocationID = () => {
  const queryClient = useQueryClient();

  const fetchGetHandoffSnapshotsByLocationID = async <E = unknown, D = GetHandoffSnapshotsByLocationIDIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseHandoffHubV1QueryEndpointArgs<'GetHandoffSnapshotsByLocationID', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetHandoffSnapshotsByLocationIDIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetHandoffSnapshotsByLocationID'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetHandoffSnapshotsByLocationIDIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetHandoffSnapshotsByLocationID'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetHandoffSnapshotsByLocationID'>({
          serviceName,
          endpointName: 'GetHandoffSnapshotsByLocationID',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaHandoffHubV1Service.GetHandoffSnapshotsByLocationID(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetHandoffSnapshotsByLocationID;
};
